import {
  getTopicMngDetail,
  topicCreate,
  TopicMngCreateParams,
  TopicMngPreCheckResult,
  TopicMngUpdateParams,
  topicPreCheck,
  topicUpdate,
} from '@/api/topicMng';
import { PageLink } from '@/config/pageConfig';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Form, Input, message, Modal, Skeleton, Space } from 'antd';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const TopicMngEditorPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const topicId = searchParams.get('id') || '';
  const [modal, modalContextHolder] = Modal.useModal();
  const [messageApi, messageContextHolder] = message.useMessage();

  const [form] = Form.useForm<Omit<TopicMngUpdateParams, 'tagId'>>();

  const { data: topicInfo, isLoading } = useQuery({
    queryKey: ['topicInfo'],
    queryFn: async () => {
      const res = await getTopicMngDetail(topicId);
      if (res.status !== 200) {
        return;
      }
      return res.data;
    },
    enabled: !!topicId,
    refetchOnWindowFocus: false,
  });

  const handleSubmit = async () => {
    // 编辑操作不需要检查
    if (topicId) {
      return handleUpdate();
    }
    // 检查是否有重复话题
    const values = form.getFieldsValue();
    const checkResult = await topicPreCheck(values.tagName);
    if (checkResult.data.result === TopicMngPreCheckResult.REPEAT) {
      createModal(checkResult.data.message);
      return;
    }
    if (checkResult.data.result === TopicMngPreCheckResult.MERGE) {
      createModal();
      return;
    }
    return handleCreate();
  };

  const createModal = (id?: string) => {
    const content = id
      ? `名称重复 (话题id: ${id}), 无法创建, 请直接编辑该话题`
      : '用户已创建过同样的话题，是否合并到官方话题?';
    modal.confirm({
      title: '存在重复的话题',
      content,
      okText: id ? '放弃创建' : '确认',
      onOk: () => {
        if (!id) {
          handleCreate();
        }
      },
    });
  };

  const handleCreate = async () => {
    const values = form.getFieldsValue();
    const params: TopicMngCreateParams = {
      tagName: values.tagName,
      description: values.description,
      artworkIds: values.artworkIds,
      recommendScore: values.recommendScore,
    };
    const res = await topicCreate(params);
    if (res.status !== 200) {
      messageApi.error(res.message);
      return;
    }
    messageApi.success('创建成功');
    setTimeout(() => {
      navigate(PageLink.TopicMng);
    }, 300);
  };

  const handleUpdate = async () => {
    const values = form.getFieldsValue();
    const params: TopicMngUpdateParams = {
      tagId: topicId,
      tagName: values.tagName,
      description: values.description,
      artworkIds: values.artworkIds,
      recommendScore: values.recommendScore,
      topIds: values.topIds,
    };
    const res = await topicUpdate(params);
    if (res.status !== 200) {
      messageApi.error(res.message);
      return;
    }
    messageApi.success('更新成功');
    setTimeout(() => {
      navigate(PageLink.TopicMng);
    }, 300);
  };

  const checkCommaSeparatedIds = (value?: string) => {
    if (!value) {
      return Promise.resolve();
    }
    const trimedValue = value.trim();
    // 检查是否为逗号分割的数字
    const pattern = /^\d+(,\d+)*$/;
    if (!pattern.test(trimedValue)) {
      return Promise.reject(new Error('请输入合法作品id'));
    }
    return Promise.resolve();
  };

  useEffect(() => {
    if (topicInfo) {
      form.setFieldsValue({
        tagName: topicInfo.tagName,
        description: topicInfo.description,
        artworkIds: topicInfo.artworkIds,
        recommendScore: topicInfo.recommendScore,
        topIds: topicInfo.topIds,
      });
    }
  }, [topicInfo, form]);

  useEffect(() => {
    if (!topicId) {
      form.resetFields();
    }
  }, [form, topicId]);

  if (isLoading) {
    return <Skeleton active />;
  }

  return (
    <div>
      {messageContextHolder}
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(PageLink.TopicMng)}
      />
      <Form
        form={form}
        style={{ margin: '20px auto 0', maxWidth: 1000 }}
        onFinish={handleSubmit}
        labelAlign="left"
        labelCol={{
          span: 2,
        }}
      >
        <Form.Item
          label="话题名称"
          name="tagName"
          required
          rules={[{ message: '请输入话题名称', required: true }]}
        >
          <Input maxLength={60} placeholder="最大60字符" disabled={!!topicId} />
        </Form.Item>
        <Form.Item label="话题描述" name="description">
          <Input.TextArea maxLength={200} placeholder="最大200字符" rows={4} />
        </Form.Item>
        <Form.Item
          label="推荐值"
          extra="填写0-100, 数字越大排序越高"
          name="recommendScore"
          required
          rules={[
            {
              message: '请输入推荐值',
              required: true,
            },
            {
              validator: (_, value) => {
                if (value === undefined || value === null) {
                  return Promise.resolve();
                }

                // 检查是否为数字
                if (isNaN(Number(value))) {
                  return Promise.reject(new Error('请输入数字'));
                }

                // 检查是否在0-100之间
                if (Number(value) < 0 || Number(value) > 100) {
                  return Promise.reject(new Error('请输入0-100的数字'));
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder="请输入推荐值"
            maxLength={3}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          label="关联作品"
          name="artworkIds"
          rules={[
            {
              validator: (_, value: string) => checkCommaSeparatedIds(value),
            },
          ]}
        >
          <Input.TextArea
            placeholder="填写作品id, 多个用英文逗号分隔, 例如: 1000,1001,1002"
            rows={4}
          />
        </Form.Item>
        {topicId && (
          <Form.Item
            label="置顶作品"
            name="topIds"
            rules={[
              {
                validator: (_, value: string) => checkCommaSeparatedIds(value),
              },
            ]}
          >
            <Input.TextArea
              placeholder="填写作品id, 多个用英文逗号分隔, 例如: 1000,1001,1002"
              rows={4}
            />
          </Form.Item>
        )}
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              {topicId ? '提交更改' : '创建新话题'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
      {modalContextHolder}
    </div>
  );
};

export default TopicMngEditorPage;
